import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import NotificationsPanel from './NotificationsPanel';
import {
    LayoutDashboard,

    Settings,
    LogOut,
    Menu,
    ChevronLeft,
    X,
    Bell,
    Search,
    Activity,
    Trello
} from 'lucide-react';

export default function Sidebar() {
    const { user, signOut } = useAuth();
    const { unreadCount } = useNotifications();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(true);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

    // Refs for click outside handling
    const notificationBtnRef = useRef<HTMLButtonElement>(null);
    const notificationPanelRef = useRef<HTMLDivElement>(null);

    // Handle click outside to close notifications
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            // Check if panel is open and click is OUTSIDE both panel and button
            if (isNotificationsOpen &&
                notificationPanelRef.current &&
                !notificationPanelRef.current.contains(event.target as Node) &&
                notificationBtnRef.current &&
                !notificationBtnRef.current.contains(event.target as Node)
            ) {
                setIsNotificationsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isNotificationsOpen]);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out', error);
        }
    };

    const toggleSidebar = () => setIsOpen(!isOpen);
    const toggleMobileSidebar = () => setIsMobileOpen(!isMobileOpen);

    return (
        <>
            {/* Mobile Header */}
            <div className="lg:hidden flex items-center justify-between p-4 bg-secondary border-b border-white/10">
                <div className="flex items-center gap-2">
                    <img src="/logo-new.png" alt="My Horizon" className="h-8 w-8 object-contain rounded-md" />
                    <span className="text-lg font-bold text-white tracking-wide">My Horizon</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg relative"
                    >
                        <Bell className="w-6 h-6" />
                        {unreadCount > 0 && (
                            <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-black" />
                        )}
                    </button>
                    {/* Mobile Notifications Panel Dropdown */}
                    {isNotificationsOpen && (
                        <div className="absolute top-16 right-2 z-50 w-80">
                            <NotificationsPanel isOpen={true} onClose={() => setIsNotificationsOpen(false)} />
                        </div>
                    )}
                    <button onClick={toggleMobileSidebar} className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg">
                        {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm" onClick={() => setIsMobileOpen(false)} />
            )}

            {/* Sidebar Container */}
            <aside className={`
                sidebar fixed lg:sticky top-0 left-0 h-screen 
                transition-all duration-300 ease-in-out z-50 flex flex-col
                bg-secondary border-r border-white/5
                ${isOpen ? 'w-64' : (isMobileOpen ? 'w-64' : 'w-20')}
                ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                {/* Logo & Toggle */}
                <div className="p-4 flex items-center justify-between h-16 border-b border-white/5">
                    <NavLink to="/" className={`flex items-center gap-3 overflow-hidden transition-all duration-300 ${isOpen ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}>
                        <img src="/logo-new.png" alt="My Horizon" className="h-8 w-8 object-contain rounded-md" />
                        <span className="text-lg font-bold text-white tracking-wide whitespace-nowrap">My Horizon</span>
                    </NavLink>
                    {/* Collapsed Icon State */}
                    {!isOpen && (
                        <NavLink to="/" className="w-full flex justify-center">
                            <img src="/logo-new.png" alt="MH" className="h-8 w-8 object-contain rounded-md" />
                        </NavLink>
                    )}

                    <button
                        onClick={toggleSidebar}
                        className={`p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white hidden md:block ${!isOpen && 'absolute right-[-12px] top-6 bg-secondary border border-white/10 shadow-lg !p-1 rounded-full'}`}
                    >
                        {isOpen ? <ChevronLeft className="w-5 h-5" /> : <Menu className="w-4 h-4" />}
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto custom-scrollbar">
                    <NavLink to="/war-room" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''} ${!isOpen && 'justify-center'}`}>
                        <nav className="relative flex items-center">
                            {/* Pulse Dot for "Live" feel */}
                            <div className="absolute -left-1 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                            <Activity className="w-5 h-5 flex-shrink-0 ml-1" />
                        </nav>
                        {isOpen && <span className="truncate ml-1 font-bold text-blue-100 uppercase tracking-wider">The War Room</span>}
                    </NavLink>

                    <NavLink to="/" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''} ${!isOpen && 'justify-center'}`}>
                        <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
                        {isOpen && <span className="truncate">Dashboard</span>}
                    </NavLink>

                    <NavLink to="/saved" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''} ${!isOpen && 'justify-center'}`}>
                        <Trello className="w-5 h-5 flex-shrink-0" />
                        {isOpen && <span className="truncate">Pipeline Board</span>}
                    </NavLink>

                    <NavLink to="/search" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''} ${!isOpen && 'justify-center'}`}>
                        <Search className="w-5 h-5 flex-shrink-0" />
                        {isOpen && <span className="truncate">Search Opportunities</span>}
                    </NavLink>

                </nav>

                {/* Footer Actions */}
                <div className="p-4 border-t border-white/5 space-y-2 relative">
                    {/* Notifications Toggle (Desktop) */}
                    <div className="">
                        <button
                            ref={notificationBtnRef}
                            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                            className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors ${!isOpen && 'justify-center'} ${isNotificationsOpen ? 'text-white bg-white/5' : ''}`}
                        >
                            <div className="relative">
                                <Bell className="w-5 h-5 flex-shrink-0" />
                                {unreadCount > 0 && (
                                    <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-black ${!isOpen && 'top-0 right-0'}`} />
                                )}
                            </div>
                            {isOpen && <span className="truncate">Notifications</span>}
                        </button>


                        {/* Correct Positioning Logic */}
                        <div
                            ref={notificationPanelRef}
                            className={`fixed bottom-4 z-50 w-96 transition-all duration-300 ${isNotificationsOpen ? 'opacity-100 translate-x-0 pointer-events-auto' : 'opacity-0 -translate-x-4 pointer-events-none'
                                }`} style={{ left: isOpen ? '270px' : '90px' }}
                        >
                            <NotificationsPanel isOpen={isNotificationsOpen} onClose={() => setIsNotificationsOpen(false)} />
                        </div>
                    </div>

                    <NavLink
                        to="/settings"
                        className={({ isActive }) => `w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors ${!isOpen && 'justify-center'} ${isActive ? 'bg-white/5 text-white' : ''}`}
                    >
                        <Settings className="w-5 h-5 flex-shrink-0" />
                        {isOpen && <span className="truncate">Settings</span>}
                    </NavLink>

                    <div className="pt-2 mt-2 border-t border-white/5">
                        <button
                            onClick={handleSignOut}
                            className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-red-500/10 text-red-400 hover:text-red-300 transition-colors ${!isOpen && 'justify-center'}`}
                        >
                            <LogOut className="w-5 h-5 flex-shrink-0" />
                            {isOpen && <span className="truncate">Sign Out</span>}
                        </button>
                    </div>

                    {isOpen && (
                        <div className="px-2 pt-2">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-gray-300">
                                    {user?.email?.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-white truncate">
                                        {user?.displayName || 'User'}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {user?.email}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}
